import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { safeStorage } from './safeStorage'
import { setChimeEnabled as soundSetChimeEnabled, setChimeVolume as soundSetChimeVolume } from '@/lib/alertSound'

// #301 — per-browser display choices. Kept apart from uiStore (theme, language) so the pages that read it
// do not pull in the language setup.
// #294 redesign — and each user's sound choices (Settings → Sounds & display → Alert sounds), saved in
// this browser the same way. A change in another tab of this browser reaches this one at once.
interface DisplayPrefsState {
  // The light that runs around unread Recent Events rows. On by default; off = the still unread look.
  // This is the "pause / stop" control for that moving content (WCAG 2.2.2).
  unreadRowLight: boolean
  /** #294 redesign — the normal alert chime on/off (default on). */
  chimeEnabled: boolean
  /** #294 redesign — the normal chime's volume, 10–100 % (default 100 % = the level before the setting). */
  chimeVolume: number
  /** #294 redesign — the critical sound's volume, 30–100 % (default 100 %): never lower, so a critical
   *  alert is never silent. */
  criticalVolume: number
  /** #368 — the dashboard's map card fills the page (the stats strip and Recent Events hidden), from xl. */
  dashboardMapExpanded: boolean
  setUnreadRowLight: (on: boolean) => void
  setChimeEnabled: (on: boolean) => void
  setChimeVolume: (percent: number) => void
  setCriticalVolume: (percent: number) => void
  setDashboardMapExpanded: (on: boolean) => void
}

type StoredPrefs = Pick<DisplayPrefsState, 'unreadRowLight' | 'chimeEnabled' | 'chimeVolume' | 'criticalVolume' | 'dashboardMapExpanded'>

const STORAGE_KEY = 'display-prefs'

function clampPercent(v: unknown, min: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 100
  return Math.min(100, Math.max(min, Math.round(v)))
}

/** The chime volume to use for any value (a hand-edited or corrupted copy included): 10–100, whole
 *  numbers; anything that is not a number reads as 100. */
export function clampChimeVolume(v: unknown): number {
  return clampPercent(v, 10)
}

/** The critical volume to use for any value: 30–100, whole numbers; anything that is not a number reads
 *  as 100. A critical alert is never silent (Review Focus 5). */
export function clampCriticalVolume(v: unknown): number {
  return clampPercent(v, 30)
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** The stored copy as safe values: a missing or wrong-typed key keeps the default. */
function readStored(stored: unknown, current: DisplayPrefsState): DisplayPrefsState {
  if (!isRecord(stored)) return current
  return {
    ...current,
    unreadRowLight: typeof stored.unreadRowLight === 'boolean' ? stored.unreadRowLight : current.unreadRowLight,
    chimeEnabled: typeof stored.chimeEnabled === 'boolean' ? stored.chimeEnabled : true,
    chimeVolume: 'chimeVolume' in stored ? clampChimeVolume(stored.chimeVolume) : current.chimeVolume,
    criticalVolume: 'criticalVolume' in stored ? clampCriticalVolume(stored.criticalVolume) : current.criticalVolume,
    dashboardMapExpanded: typeof stored.dashboardMapExpanded === 'boolean' ? stored.dashboardMapExpanded : false,   // #368 — like chimeEnabled: the default, not the in-memory value
  }
}

export const useDisplayPrefs = create<DisplayPrefsState>()(
  persist(
    (set) => ({
      unreadRowLight: true,
      chimeEnabled: true,
      chimeVolume: 100,
      criticalVolume: 100,
      dashboardMapExpanded: false,
      setUnreadRowLight: (on) => set({ unreadRowLight: on }),
      setChimeEnabled: (on) => set({ chimeEnabled: on }),
      setChimeVolume: (percent) => set({ chimeVolume: clampChimeVolume(percent) }),
      setCriticalVolume: (percent) => set({ criticalVolume: clampCriticalVolume(percent) }),
      setDashboardMapExpanded: (on) => set({ dashboardMapExpanded: on }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => safeStorage),
      partialize: (state): StoredPrefs => ({
        unreadRowLight: state.unreadRowLight,
        chimeEnabled: state.chimeEnabled,
        chimeVolume: state.chimeVolume,
        criticalVolume: state.criticalVolume,
        dashboardMapExpanded: state.dashboardMapExpanded,
      }),
      // Clamped on every read of the stored copy (load, and another tab's change).
      merge: (stored, current) => readStored(stored, current),
    },
  ),
)

// The sound module plays the chime: hand it the switch and the volume now, on every change, and when
// another tab's change is read in.
function handToSound(state: DisplayPrefsState) {
  soundSetChimeEnabled(state.chimeEnabled)
  soundSetChimeVolume(state.chimeVolume / 100)
}
handToSound(useDisplayPrefs.getState())
useDisplayPrefs.subscribe((state, prev) => {
  if (state.chimeEnabled !== prev.chimeEnabled || state.chimeVolume !== prev.chimeVolume) handToSound(state)
})

// Another tab changed the stored copy: read it in, so every tab plays at the same volumes at once.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) void useDisplayPrefs.persist.rehydrate()
  })
}
